import {
  Button,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { buy_nfts, cancel_nft_sell } from "../../keychain/login";
import { rutas } from "../../rutas/rutas";

export const Market = () => {
  const { usuario, especiales = [], generalNft = [] } = useSelector((state) => state.userSlice);

  const [selector, setSelector] = useState(null);
  const [open2, setOpen2] = useState(false);
  const [cartas, setCartas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cargado, setCargado] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [tokenFilter, setTokenFilter] = useState("all");
  const [sellerFilter, setSellerFilter] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("default");

  const handleClose2 = () => {
    setOpen2(false);
    setSelector(null);
  };

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const getCardType = (card) => {
    return card?.marketType || "general";
  };

  const getFamily = (card) => {
    return (
      card?.family ||
      card?.familia ||
      card?.grupo ||
      card?.group ||
      card?.category ||
      card?.categoria ||
      "unknown"
    );
  };

  const getName = (card) => {
    return card?.nombre || card?.name || `Card #${card?.ide || "?"}`;
  };

  const getRarity = (card) => {
    return card?.rareza || card?.rarity || "";
  };

  const getCode = (card) => {
    return card?.id || "";
  };

  const getLowestPrice = (nftList = []) => {
    if (!Array.isArray(nftList) || nftList.length === 0) return 0;
    return Math.min(...nftList.map((item) => Number(item?.precio) || 0));
  };

  const getHighestPrice = (nftList = []) => {
    if (!Array.isArray(nftList) || nftList.length === 0) return 0;
    return Math.max(...nftList.map((item) => Number(item?.precio) || 0));
  };

  const getAvailableTokens = (nftList = []) => {
    return [...new Set(nftList.map((item) => item?.simbolo).filter(Boolean))];
  };

  const getCardSellerList = (nftList = []) => {
    return [...new Set(nftList.map((item) => item?.vendedor).filter(Boolean))];
  };

  const handleTransform = (market = []) => {
    const especialesIds = new Set(especiales.map((card) => card?.ide).filter(Boolean));
    const generalIds = new Set(generalNft.map((card) => card?.ide).filter(Boolean));
    const deck = [
      ...especiales.map((item) => ({ ...item, marketType: "special" })),
      ...generalNft.map((item) => ({ ...item, marketType: "general" })),
    ];

    const featuredFirst = [105];

    if (deck.length === 0) {
      console.warn("Base deck is empty, NFTs cannot be mapped correctly");
    }

    const transformed = market.reduce((acum, nft) => {
      const id = parseInt(nft?.grouping?.ID);
      if (!id) return acum;

      const cardBase = deck.find((card) => card?.ide === id);
      if (!cardBase) return acum;

      let detectedType = "general";
      if (especialesIds.has(id)) detectedType = "special";
      else if (generalIds.has(id)) detectedType = "general";

      const nftData = {
        grupo: nft.grouping,
        nftId: nft.nftId,
        precio: nft.price,
        simbolo: nft.priceSymbol,
        vendedor: nft.account,
      };

      const existingIndex = acum.findIndex((card) => card.ide === id);

      if (existingIndex === -1) {
        return [
          ...acum,
          {
            ...cardBase,
            marketType: detectedType,
            nft: [nftData],
          },
        ];
      }

      return acum.map((card) =>
        card.ide === id
          ? {
              ...card,
              nft: [...card.nft, nftData],
            }
          : card
      );
    }, []);

    return transformed.sort((a, b) =>
      featuredFirst.includes(a.ide) ? -1 : featuredFirst.includes(b.ide) ? 1 : 0
    );
  };

  const getCard = () => {
    setLoading(true);

    axios
      .post(`${rutas.smartContract}`, {
        jsonrpc: "2.0",
        id: 1,
        method: "find",
        params: {
          contract: "nftmarket",
          table: "NFTHUESOsellBook",
          query: {},
          limit: 1000,
          offset: 0,
          indexes: [],
        },
      })
      .then((res) => {
        const deckSave = res?.data?.result || [];

        if (deckSave.length === 0) {
          console.warn("Empty market response, retrying in 3 seconds");
          setTimeout(getCard, 3000);
          return;
        }

        const transformed = handleTransform(deckSave);
        setCartas(transformed);
        setLoading(false);
        setCargado(true);
      })
      .catch((err) => {
        console.error("Error loading market cards:", err);
        setCartas([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    if ((especiales.length > 0 || generalNft.length > 0) && !cargado) {
      getCard();
    }
  }, [especiales, generalNft, cargado]);

  const familyOptions = useMemo(() => {
    return [
      "all",
      ...new Set(
        cartas
          .map((card) => getFamily(card))
          .filter((item) => item && normalizeText(item) !== "unknown")
      ),
    ];
  }, [cartas]);

  const tokenOptions = useMemo(() => {
    return [
      "all",
      ...new Set(cartas.flatMap((card) => getAvailableTokens(card?.nft || []))),
    ];
  }, [cartas]);

  const filteredCartas = useMemo(() => {
    let result = [...cartas];

    if (search.trim()) {
      const value = normalizeText(search);
      result = result.filter((card) => {
        const name = normalizeText(getName(card));
        const id = normalizeText(card?.ide);
        const code = normalizeText(getCode(card));
        const family = normalizeText(getFamily(card));
        return (
          name.includes(value) ||
          id.includes(value) ||
          code.includes(value) ||
          family.includes(value)
        );
      });
    }

    if (rarityFilter !== "all") {
      result = result.filter(
        (card) => normalizeText(getRarity(card)) === normalizeText(rarityFilter)
      );
    }

    if (typeFilter !== "all") {
      result = result.filter(
        (card) => normalizeText(getCardType(card)) === normalizeText(typeFilter)
      );
    }

    if (familyFilter !== "all") {
      result = result.filter(
        (card) => normalizeText(getFamily(card)) === normalizeText(familyFilter)
      );
    }

    if (tokenFilter !== "all") {
      result = result.filter((card) =>
        getAvailableTokens(card?.nft || []).some(
          (token) => normalizeText(token) === normalizeText(tokenFilter)
        )
      );
    }

    if (sellerFilter.trim()) {
      const sellerValue = normalizeText(sellerFilter);
      result = result.filter((card) =>
        getCardSellerList(card?.nft || []).some((seller) =>
          normalizeText(seller).includes(sellerValue)
        )
      );
    }

    if (minPrice !== "") {
      const min = Number(minPrice);
      result = result.filter((card) => getLowestPrice(card?.nft || []) >= min);
    }

    if (maxPrice !== "") {
      const max = Number(maxPrice);
      result = result.filter((card) => getLowestPrice(card?.nft || []) <= max);
    }

    switch (sortBy) {
      case "name-asc":
        result.sort((a, b) => getName(a).localeCompare(getName(b)));
        break;
      case "name-desc":
        result.sort((a, b) => getName(b).localeCompare(getName(a)));
        break;
      case "id-asc":
        result.sort((a, b) => (a?.ide || 0) - (b?.ide || 0));
        break;
      case "id-desc":
        result.sort((a, b) => (b?.ide || 0) - (a?.ide || 0));
        break;
      case "price-asc":
        result.sort((a, b) => getLowestPrice(a?.nft || []) - getLowestPrice(b?.nft || []));
        break;
      case "price-desc":
        result.sort((a, b) => getLowestPrice(b?.nft || []) - getLowestPrice(a?.nft || []));
        break;
      case "rarity-asc":
        result.sort((a, b) => getRarity(a).localeCompare(getRarity(b)));
        break;
      case "rarity-desc":
        result.sort((a, b) => getRarity(b).localeCompare(getRarity(a)));
        break;
      default:
        break;
    }

    return result;
  }, [
    cartas,
    search,
    rarityFilter,
    typeFilter,
    familyFilter,
    tokenFilter,
    sellerFilter,
    minPrice,
    maxPrice,
    sortBy,
  ]);

  const resetFilters = () => {
    setSearch("");
    setRarityFilter("all");
    setTypeFilter("all");
    setFamilyFilter("all");
    setTokenFilter("all");
    setSellerFilter("");
    setMinPrice("");
    setMaxPrice("");
    setSortBy("default");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        p: { xs: 1, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      {/* Filters */}
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 1.2,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputLabelProps={{ style: { color: "white" } }}
          sx={inputStyle}
        />

        <FormControl fullWidth size="small" sx={inputStyle}>
          <InputLabel sx={{ color: "white" }}>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="special">Special</MenuItem>
            <MenuItem value="general">General</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={inputStyle}>
          <InputLabel sx={{ color: "white" }}>Rarity</InputLabel>
          <Select
            value={rarityFilter}
            label="Rarity"
            onChange={(e) => setRarityFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="c">Common</MenuItem>
            <MenuItem value="r">Rare</MenuItem>
            <MenuItem value="e">Epic</MenuItem>
            <MenuItem value="l">Legendary</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={inputStyle}>
          <InputLabel sx={{ color: "white" }}>Family</InputLabel>
          <Select
            value={familyFilter}
            label="Family"
            onChange={(e) => setFamilyFilter(e.target.value)}
          >
            {familyOptions.map((item) => (
              <MenuItem key={item} value={item}>
                {item === "all" ? "All" : item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={inputStyle}>
          <InputLabel sx={{ color: "white" }}>Sale Token</InputLabel>
          <Select
            value={tokenFilter}
            label="Sale Token"
            onChange={(e) => setTokenFilter(e.target.value)}
          >
            {tokenOptions.map((item) => (
              <MenuItem key={item} value={item}>
                {item === "all" ? "All" : item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Seller"
          size="small"
          value={sellerFilter}
          onChange={(e) => setSellerFilter(e.target.value)}
          InputLabelProps={{ style: { color: "white" } }}
          sx={inputStyle}
        />

        <TextField
          fullWidth
          type="number"
          label="Min Price"
          size="small"
          value={minPrice}
          onChange={(e) => setMinPrice(e.target.value)}
          InputLabelProps={{ style: { color: "white" } }}
          sx={inputStyle}
        />

        <TextField
          fullWidth
          type="number"
          label="Max Price"
          size="small"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
          InputLabelProps={{ style: { color: "white" } }}
          sx={inputStyle}
        />

        <FormControl fullWidth size="small" sx={inputStyle}>
          <InputLabel sx={{ color: "white" }}>Sort By</InputLabel>
          <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="name-asc">Name A-Z</MenuItem>
            <MenuItem value="name-desc">Name Z-A</MenuItem>
            <MenuItem value="id-asc">ID Low to High</MenuItem>
            <MenuItem value="id-desc">ID High to Low</MenuItem>
            <MenuItem value="price-asc">Lowest Price</MenuItem>
            <MenuItem value="price-desc">Highest Price</MenuItem>
            <MenuItem value="rarity-asc">Rarity A-Z</MenuItem>
            <MenuItem value="rarity-desc">Rarity Z-A</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={resetFilters}
          sx={{
            minHeight: "40px",
            fontWeight: "bold",
          }}
        >
          Reset Filters
        </Button>
      </Box>

      {/* Result info */}
      <Box sx={{ mb: 1, px: 0.5 }}>
        <Typography sx={{ color: "white", fontSize: { xs: "12px", sm: "14px" } }}>
          Results: {filteredCartas.length}
        </Typography>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          overflow: "auto",
          pr: 0.5,
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "250px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
                lg: "repeat(5, 1fr)",
                xl: "repeat(6, 1fr)",
              },
              gap: { xs: 1, sm: 2 },
              width: "100%",
              alignContent: "flex-start",
              justifyItems: "center",
              pb: 2,
            }}
          >
            {filteredCartas.map((carta) => {
              const rarityValue = getRarity(carta);
              const lowestPrice = getLowestPrice(carta?.nft || []);
              const tokenList = getAvailableTokens(carta?.nft || []);

              return (
                <Box
                  key={carta?.ide}
                  sx={{
                    width: { xs: "100%", sm: "125px" },
                    maxWidth: "140px",
                    position: "relative",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={carta?.url || ""}
                    alt={getName(carta)}
                    sx={{
                      width: "100%",
                      height: { xs: "165px", sm: "190px" },
                      objectFit: "cover",
                      cursor: "pointer",
                      borderRadius: 1.2,
                    }}
                    onClick={() => {
                      setSelector(carta);
                      setOpen2(true);
                    }}
                  />

                  <Typography
                    sx={{
                      background: getCode(carta)?.[0] === "E" ? "#2B21DE" : "#F50202",
                      position: "absolute",
                      top: 4,
                      left: 4,
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      textAlign: "center",
                      color: "white",
                      fontSize: "12px",
                      lineHeight: "22px",
                      fontWeight: "bold",
                    }}
                  >
                    {getCode(carta)?.[0] || "?"}
                  </Typography>

                  <Box
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: getCode(carta)?.[0] === "E" ? "#2B21DE" : "#F50202",
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      textAlign: "center",
                      color: "white",
                      fontSize: "12px",
                      lineHeight: "22px",
                      fontWeight: "bold",
                    }}
                  >
                    {String(rarityValue || "?").toUpperCase()}
                  </Box>

                  <Typography
                    sx={{
                      mt: 0.7,
                      color: "white",
                      fontSize: { xs: "11px", sm: "12px" },
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getName(carta)}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#cfcfcf",
                      fontSize: { xs: "10px", sm: "11px" },
                      textAlign: "center",
                    }}
                  >
                    From {lowestPrice.toFixed(2)}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#9ad1ff",
                      fontSize: { xs: "10px", sm: "11px" },
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {tokenList.join(", ") || "No token"}
                  </Typography>
                </Box>
              );
            })}

            {!loading && filteredCartas.length === 0 && (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <Typography sx={{ color: "white", textAlign: "center" }}>
                  No cards found with the selected filters.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Modal */}
      {open2 && selector && (
        <Dialog
          open={open2}
          onClose={handleClose2}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              bgcolor: "#1c1c1c",
              color: "white",
              borderRadius: 2,
              mx: { xs: 1, sm: 2 },
              width: { xs: "95%", sm: "90%" },
            },
          }}
        >
          <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
                gap: 2,
                width: "100%",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <CardMedia
                  component="img"
                  image={selector?.url || ""}
                  alt={getName(selector)}
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "220px", md: "100%" },
                    margin: "0 auto",
                    borderRadius: 1,
                    objectFit: "cover",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { xs: "280px", md: "360px" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "18px", sm: "22px" },
                    fontWeight: "bold",
                    mb: 1,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  {getName(selector)}
                </Typography>

                <Typography sx={{ color: "#cfcfcf", mb: 0.5, fontSize: "13px" }}>
                  Type: {getCardType(selector)}
                </Typography>

                <Typography sx={{ color: "#cfcfcf", mb: 0.5, fontSize: "13px" }}>
                  Family: {getFamily(selector)}
                </Typography>

                <Typography sx={{ color: "#cfcfcf", mb: 1, fontSize: "13px" }}>
                  Rarity: {String(getRarity(selector) || "").toUpperCase()}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: { xs: "300px", md: "340px" },
                    pr: 0.5,
                  }}
                >
                  {selector?.nft?.length > 0 ? (
                    selector.nft.map((nft) => (
                      <Box
                        key={nft.nftId}
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: "space-between",
                          alignItems: { xs: "stretch", sm: "center" },
                          gap: 1,
                          borderBottom: "1px solid rgba(255,255,255,0.15)",
                          py: 1.2,
                        }}
                      >
                        <Box>
                          <Typography sx={{ color: "white", fontSize: "14px" }}>
                            Price: {parseFloat(nft.precio).toFixed(2)} {nft.simbolo}
                          </Typography>
                          <Typography sx={{ color: "#bdbdbd", fontSize: "12px" }}>
                            Seller: {nft.vendedor}
                          </Typography>
                        </Box>

                        {usuario?.user !== nft.vendedor ? (
                          <Button
                            variant="contained"
                            onClick={() =>
                              buy_nfts(nft.nftId, nft.precio, nft.simbolo, "comprar")
                            }
                            sx={{
                              minWidth: { xs: "100%", sm: "90px" },
                            }}
                          >
                            Buy
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => cancel_nft_sell(nft.nftId)}
                            sx={{
                              minWidth: { xs: "100%", sm: "90px" },
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ color: "white" }}>No NFTs available</Typography>
                  )}
                </Box>

                <Button sx={{ mt: 2 }} fullWidth variant="contained" onClick={handleClose2}>
                  Close
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    color: "white",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.3)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.5)",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
  },
};